import React from "react";
import { Modal, View, Pressable, StyleSheet } from "react-native";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// Modal for dark store sheets and pop-ups, aware of the system bars and the
// keyboard. Always edge-to-edge (status + navigation bar translucent, the
// pattern of MyTasks' modals), so it lays out the same on every Android
// version: on targetSdk 36 the modal window is edge-to-edge anyway.
//   bottom     sheet: its own padding plus the bottom inset, so the soft nav
//              buttons / gesture bar never cover the last row; the sheet's
//              background runs under the bar.
//   center     pop-up: kept between the status and navigation bars.
//   fullscreen page: padded by the top and bottom insets.
// The content rises above the keyboard; for a sheet the keyboard already
// covers the nav bar, so its inset is taken off the lift.

const POSITION = {
  BOTTOM: "bottom",
  CENTER: "center",
  FULLSCREEN: "fullscreen",
};

const padOf = (style, inset) =>
  (StyleSheet.flatten(style)?.paddingBottom ?? 0) + inset;

const DsModal = ({
  visible,
  onClose,
  position = POSITION.BOTTOM,
  animationType,
  scrim = "rgba(0,0,0,0.45)",
  contentStyle,
  children,
}) => {
  const insets = useSafeAreaInsets();
  const isSheet = position === POSITION.BOTTOM;
  const isFull = position === POSITION.FULLSCREEN;

  const rootStyle = isFull
    ? [
        styles.root,
        {
          paddingTop: insets.top,
          backgroundColor: StyleSheet.flatten(contentStyle)?.backgroundColor,
        },
      ]
    : [
        styles.root,
        { backgroundColor: scrim },
        isSheet
          ? styles.bottom
          : [
              styles.center,
              { paddingTop: insets.top, paddingBottom: insets.bottom },
            ],
      ];
  const bodyStyle = isSheet
    ? [contentStyle, { paddingBottom: padOf(contentStyle, insets.bottom) }]
    : contentStyle;

  return (
    <Modal
      visible={visible}
      transparent={!isFull}
      statusBarTranslucent
      navigationBarTranslucent
      animationType={animationType || (isSheet || isFull ? "slide" : "fade")}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={isSheet ? insets.bottom : 0}
        style={rootStyle}
      >
        {isFull ? null : (
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        )}
        {isFull ? (
          <View
            style={[
              styles.full,
              contentStyle,
              { paddingBottom: padOf(contentStyle, insets.bottom) },
            ]}
          >
            {children}
          </View>
        ) : (
          <View style={bodyStyle}>{children}</View>
        )}
      </KeyboardAvoidingView>
    </Modal>
  );
};

DsModal.POSITION = POSITION;

export default DsModal;

const styles = StyleSheet.create({
  root: { flex: 1 },
  bottom: { justifyContent: "flex-end" },
  center: { justifyContent: "center" },
  full: { flex: 1 },
});
